import {
  Badge,
  BrandLoader,
  Button,
  Flex,
  FormatDeliveryDate,
  Icon,
  Text,
  Tooltip,
} from '@wyz/ui';
import {
  DateUtils,
  DEFAULT_TIRE_BRAND_LOGO,
  DEFAULT_TIRE_BRAND_WIDTH,
  getBrandLogoUrl,
  ORDER_TYPE_CODE,
  useBoolean,
} from '@wyz/utils';
import clsx from 'clsx';
import { Trans, useConfig, useLocale } from '~/common/context';
import {
  Img,
  PurchaseOfferDescriptionCell,
  SeasonIcon,
  WcwDetailModal,
} from '~/purchase/components';
import { WcwSearchHelpers } from '~/purchase/helpers';
import { IWcwOffer } from '~/purchase/types';

type Props = {
  product: IWcwOffer;
  offer: IWcwOffer['offers'][number];
  offerIndex: number;
  offersCount: number;
  showCommon: boolean;
  viewPrice: boolean;
  isAdding: boolean;
};

function WcwOfferRow({
  product,
  offer,
  offerIndex,
  offersCount,
  showCommon,
  viewPrice,
  isAdding,
}: Props) {
  const locale = useLocale();
  const [detailOpened, detailHandlers] = useBoolean();
  const { cdnUrl } = useConfig();

  return (
    <tr
      className={clsx('text-nowrap fs-xs position-relative', {
        subhead: showCommon,
        main: offerIndex === offersCount - 1,
      })}
    >
      {showCommon && (
        <td
          id='product'
          rowSpan={offersCount}
          className='align-middle p-3 text-wrap'
        >
          <Flex alignItems='start' className='gap-2'>
            <Flex direction='column' alignItems='start' className='gap-1'>
              <Img
                src={product.wheel.image}
                alt={product.wheel.name}
                width={90}
                height={90}
                className='object-fit-contain flex-shrink-0'
                baseUrl={cdnUrl}
                defaultImage={WcwSearchHelpers.DEFAULT_WHEEL_IMAGE}
              />
              <Tooltip label={product.tyre.brand.name}>
                <Img
                  src={getBrandLogoUrl(product.tyre.brand.name)}
                  baseUrl={cdnUrl}
                  defaultImage={DEFAULT_TIRE_BRAND_LOGO}
                  alt={product.tyre.brand.name}
                  width={DEFAULT_TIRE_BRAND_WIDTH.sm}
                  loader={BrandLoader}
                  className='mt-1'
                />
              </Tooltip>
            </Flex>
            <div>
              <Text
                weight='bold'
                className='text-uppercase'
                heading='sm'
                color='tertiary'
              >
                {product.wheel.name}
              </Text>
              <Flex alignItems='center' className='gap-1 mt-1'>
                <Badge pill bg='info' className='fs-xxs fw-semi-bold'>
                  {product.tyre.dimension.fullSize}
                </Badge>
                <SeasonIcon value={product.tyre.season.publicKey} size='xs' />
              </Flex>
              <Text heading='xxs' color='tertiary' className='mt-1'>
                {product.tyre.profile.design1}
              </Text>
              <Text heading='xxs' color='secondary-gray'>
                <Trans
                  i18nKey='common.tyre.ref_value'
                  values={{
                    value: product.wheel.reference,
                  }}
                />
              </Text>

              <Flex className='gap-3 mt-1'>
                <Text heading='xxs' color='secondary-gray'>
                  <Trans i18nKey='app.common.product.diameter' />
                  {' : '}
                  <Badge pill bg='info'>
                    {product.wheel.rimDiameter}
                  </Badge>
                </Text>
                <Text heading='xxs' color='secondary-gray'>
                  <Trans i18nKey='app.common.wheel.rim_type' />
                  {' : '}
                  <Badge pill bg='info'>
                    {product.wheel.rimType}
                  </Badge>
                </Text>
              </Flex>
            </div>
          </Flex>
        </td>
      )}
      {showCommon && (
        <td
          id='description'
          rowSpan={offersCount}
          className='align-middle text-center p-2'
        >
          <PurchaseOfferDescriptionCell
            tyre={product.tyre}
            onDetail={detailHandlers.setTrue}
          />
        </td>
      )}
      {showCommon && (
        <td
          id='recommended-price'
          rowSpan={offersCount}
          className='align-middle text-center'
        >
          <Text heading='xs' weight='semi-bold' color='primary'>
            {offer.offerMetadata.recommendedPrice != null
              ? locale.formatters.money(offer.offerMetadata.recommendedPrice, {
                  currency: offer.offerMetadata.currency,
                })
              : '/'}
          </Text>
        </td>
      )}
      <td id='supplier' className='p-0 no-border align-middle text-center'>
        <Text
          heading='xs'
          weight='semi-bold'
          color='tertiary'
          className='px-2 text-wrap'
        >
          {offer.supplier.name}
        </Text>
      </td>
      <td id='delivery' className='p-0 no-border align-middle text-center'>
        <Text heading='xs' weight='semi-bold' color='primary'>
          <FormatDeliveryDate
            orderType={
              offer.offerMetadata.isBackOrder ? ORDER_TYPE_CODE.BO : null
            }
            backorderContent={
              <Trans i18nKey='app.common.offer_type.backorder' />
            }
            date={offer.offerMetadata.deliveryDate}
            format={DateUtils.DATE_FORMAT.DM}
            locale={locale.locale}
          />
        </Text>
      </td>
      <td id='quantity' className='p-0 no-border align-middle text-center'>
        <Text heading='sm' weight='semi-bold' color='primary'>
          <Trans
            i18nKey='app.purchase.wcw.search_page.set_option'
            values={{ count: product.wheel.salesUnit }}
          />
        </Text>
      </td>
      {viewPrice && (
        <td id='price-ht' className='p-0 no-border align-middle text-center'>
          <Text heading='sm' weight='semi-bold' color='primary'>
            {locale.formatters.money(
              offer.offerMetadata.priceExclTax * product.wheel.salesUnit,
              {
                currency: offer.offerMetadata.currency,
              },
            )}
          </Text>
        </td>
      )}
      {showCommon && (
        <td id='action' rowSpan={offersCount} className='align-middle p-0'>
          <Flex
            className='p-1 gap-2'
            direction='column'
            justifyContent='center'
            alignItems='center'
          >
            <Button
              id={`${product.id}-add-to-cart`}
              variant='primary'
              size='sm'
              type='submit'
              form={product.id}
              disabled={!product.canOrder}
              isLoading={isAdding}
            >
              <span className='me-1 add-to-cart-text'>
                <Trans i18nKey='app.common.button.add' />
              </span>
              <Icon icon='shopping-cart' size='xs' />
            </Button>
          </Flex>
        </td>
      )}
      {detailOpened && (
        <WcwDetailModal offer={product} onClose={detailHandlers.setFalse} />
      )}
    </tr>
  );
}

export default WcwOfferRow;
